/** @jsxImportSource @emotion/react */
import React, { useCallback } from 'react';
import { Box, Flex } from 'rebass';
import { Formik } from 'formik';
import { bool, func } from 'prop-types';
import { css, keyframes } from '@emotion/react';
import {
  ModalStyled,
  ModalActionsStyled,
  CheckboxSetStyled,
  CheckboxStyled,
  TextStyled,
  ButtonStyled,
  useTokens
} from '@nike/nike-design-system-components';

import DatePicker from '../../common/DatePicker';

import Spinner from '../../../components/spinner/Spinner';

import { MandatoryFields } from '../utils';
import { ORDER_TYPES, ORDER_TYPE_NAMES } from '../constants';
import { initialValues, validationSchema } from './constants';

const { ATONCE, FUTURES, FORECAST } = ORDER_TYPE_NAMES;

const fadeIn = keyframes`from { opacity: 0 } to { opacity: 1 }`;

const DateChangeModal = ({ isOpen, closeModal, submitDateChange }) => {
  const tokens = useTokens();

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={submitDateChange}>
      {({
        values,
        handleChange,
        touched,
        errors,
        handleBlur,
        setFieldValue,
        resetForm,
        handleSubmit,
        isValid,
        dirty,
        isSubmitting
      }) => {
        const doesOrderTypesInclude = orderType => values.orderTypes.includes(orderType);

        const getFieldError = field => touched[field] && errors[field];

        const orderTypesError = getFieldError('orderTypes');

        const atOnceStartError = getFieldError('atOnceStart');
        const atOnceEndError = getFieldError('atOnceEnd');

        const futuresCrdError = getFieldError('futuresCrd');
        const futuresStartError = getFieldError('futuresStart');
        const futuresEndError = getFieldError('futuresEnd');

        const forecastCrdError = getFieldError('forecastCrd');
        const forecastStartError = getFieldError('forecastStart');
        const forecastEndError = getFieldError('forecastEnd');

        const handleOrderTypesChange = useCallback(
          ({ target: { id, checked } }) => {
            const newValues = checked ? [...values.orderTypes, id] : values.orderTypes.filter(type => type !== id);
            setFieldValue('orderTypes', newValues);

            if (!newValues.includes(ATONCE)) {
              setFieldValue('atOnceStart', '');
              setFieldValue('atOnceEnd', '');
            }

            if (!newValues.includes(FUTURES)) {
              setFieldValue('futuresCrd', '');
              setFieldValue('futuresStart', '');
              setFieldValue('futuresEnd', '');
            }

            if (!newValues.includes(FORECAST)) {
              setFieldValue('forecastCrd', '');
              setFieldValue('forecastStart', '');
              setFieldValue('forecastEnd', '');
            }
          },
          [setFieldValue, values.orderTypes]
        );

        return (
          <ModalStyled
            isOpen={isOpen}
            closeModal={closeModal}
            title=''
            modalRootCompProps={{ style: { maxWidth: '1000px' } }}
            backdropCompProps={{ style: { backdropFilter: 'blur(4px)' } }}
            slots={{
              HeaderTitleSlot: (
                <Box width='140%' pb='20px' style={{ borderBottom: `1px solid ${tokens.podiumCdsColorGrey300}` }}>
                  <TextStyled appearance='title2'>Date Change</TextStyled>
                  <TextStyled appearance='body1Strong' color={tokens.podiumCdsColorGrey500}>
                    The selections below will be applied and overwrite any existing catalog dates.
                  </TextStyled>
                </Box>
              ),
              FooterSlot: (
                <ModalActionsStyled>
                  {isSubmitting ? (
                    <Spinner className='relative-container' />
                  ) : (
                    <>
                      <ButtonStyled
                        type='submit'
                        appearance='primary'
                        onClick={handleSubmit}
                        disabled={!isValid || !dirty}
                      >
                        Apply Changes
                      </ButtonStyled>
                      <ButtonStyled type='button' appearance='secondary' onClick={closeModal}>
                        Cancel
                      </ButtonStyled>
                    </>
                  )}
                </ModalActionsStyled>
              )
            }}
          >
            <Box width='100%' height='100%'>
              <Box pb='10px'>
                <Box pb='12px'>
                  <TextStyled appearance='title4'>
                    Order Type(s) <MandatoryFields color={orderTypesError ? 'red' : 'black'} />
                  </TextStyled>

                  <CheckboxSetStyled
                    disabled={false}
                    error={orderTypesError}
                    errorMessage={errors.orderTypes}
                    hasLargeLegend={false}
                    inline='inline'
                    onChange={handleOrderTypesChange}
                    selectedValues={values.orderTypes}
                    supportText='Note: either Futures or Forecast can be selected at a time, but not both'
                  >
                    {ORDER_TYPES.map(orderType => (
                      <CheckboxStyled
                        key={orderType.value}
                        id={orderType.value}
                        disabled={
                          (orderType.value === FUTURES && doesOrderTypesInclude(FORECAST)) ||
                          (orderType.value === FORECAST && doesOrderTypesInclude(FUTURES))
                        }
                        label={orderType.label}
                        value={orderType.value}
                      />
                    ))}
                  </CheckboxSetStyled>

                  <ButtonStyled
                    appearance='text'
                    background='light'
                    ariaLabel='Clear All Inputs'
                    type='button'
                    onClick={() => resetForm()}
                    style={{ margin: '12px 0 0 12px' }}
                  >
                    Clear All
                  </ButtonStyled>
                </Box>

                <div
                  css={css`
                    animation: ${fadeIn} 0.5s ease;
                    display: ${doesOrderTypesInclude(ATONCE) ? 'block' : 'none'};
                  `}
                  data-testid='atOnceFormSection'
                >
                  <TextStyled appearance='title4' style={{ margin: '10px 0' }}>
                    At-Once Dates <MandatoryFields color={atOnceStartError || atOnceEndError ? 'red' : 'black'} />
                  </TextStyled>

                  <Flex flexDirection='row'>
                    <DatePicker
                      id='atOnceStart'
                      error={atOnceStartError}
                      errorMessage={errors.atOnceStart}
                      label='Display Start Date'
                      name='atOnceStart'
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.atOnceStart}
                      min={null}
                      max={values.atOnceEnd || null}
                    />
                    <Box p='12px' />
                    <DatePicker
                      id='atOnceEnd'
                      error={atOnceEndError}
                      errorMessage={errors.atOnceEnd}
                      label='Display End Date'
                      name='atOnceEnd'
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.atOnceEnd}
                      min={values.atOnceStart || null}
                    />
                  </Flex>
                </div>

                <div
                  css={css`
                    animation: ${fadeIn} 0.5s ease;
                    display: ${doesOrderTypesInclude(FUTURES) ? 'flex' : 'none'};
                    flex-direction: column;
                  `}
                  data-testid='futuresFormSection'
                >
                  <TextStyled appearance='title4' style={{ margin: '10px 0' }}>
                    Futures Dates{' '}
                    <MandatoryFields
                      color={futuresCrdError || futuresStartError || futuresEndError ? 'red' : 'black'}
                    />
                  </TextStyled>

                  <Flex>
                    <Box width='50%' pr='12px'>
                      <DatePicker
                        id='futuresCrd'
                        error={futuresCrdError}
                        errorMessage={errors.futuresCrd}
                        label='CRD Date'
                        name='futuresCrd'
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.futuresCrd}
                        min={null}
                      />
                    </Box>
                  </Flex>

                  <Flex flexDirection='row'>
                    <DatePicker
                      id='futuresStart'
                      error={futuresStartError}
                      errorMessage={errors.futuresStart}
                      label='Display Start Date'
                      name='futuresStart'
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.futuresStart}
                      min={null}
                      max={values.futuresEnd || null}
                    />
                    <Box p='12px' />
                    <DatePicker
                      id='futuresEnd'
                      error={futuresEndError}
                      errorMessage={errors.futuresEnd}
                      label='Display End Date'
                      name='futuresEnd'
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.futuresEnd}
                      min={values.futuresStart || null}
                    />
                  </Flex>
                </div>

                <div
                  css={css`
                    animation: ${fadeIn} 0.5s ease;
                    display: ${doesOrderTypesInclude(FORECAST) ? 'flex' : 'none'};
                    flex-direction: column;
                  `}
                  data-testid='forecastFormSection'
                >
                  <TextStyled appearance='title4' style={{ margin: '10px 0' }}>
                    Forecast Dates{' '}
                    <MandatoryFields
                      color={forecastCrdError || forecastStartError || forecastEndError ? 'red' : 'black'}
                    />
                  </TextStyled>

                  <Flex>
                    <Box width='50%' pr='12px'>
                      <DatePicker
                        id='forecastCrd'
                        error={forecastCrdError}
                        errorMessage={errors.forecastCrd}
                        label='CRD Date'
                        name='forecastCrd'
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.forecastCrd}
                        min={null}
                      />
                    </Box>
                  </Flex>

                  <Flex flexDirection='row'>
                    <DatePicker
                      id='forecastStart'
                      error={forecastStartError}
                      errorMessage={errors.forecastStart}
                      label='Display Start Date'
                      name='forecastStart'
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.forecastStart}
                      min={null}
                      max={values.forecastEnd || null}
                    />
                    <Box p='12px' />
                    <DatePicker
                      id='forecastEnd'
                      error={forecastEndError}
                      errorMessage={errors.forecastEnd}
                      label='Display End Date'
                      name='forecastEnd'
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.forecastEnd}
                      min={values.forecastStart || null}
                    />
                  </Flex>
                </div>
              </Box>
            </Box>
          </ModalStyled>
        );
      }}
    </Formik>
  );
};

DateChangeModal.propTypes = {
  isOpen: bool.isRequired,
  closeModal: func.isRequired,
  submitDateChange: func.isRequired
};

export default DateChangeModal;
